import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import {
  Form,
  Input,
  Button,
  FormFeedback,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { cookieParams, isLoggedIn } from '../api/Auth';

import { isValidEmail } from '../helpers/Validators';
import ProcessingSpinner from '../components/ProcessingSpinner';

const PageUnlockAccess = (({
  page,
  providePassword,
  passwordError,
  onSubmit,
}) => {
  const intl = useIntl();

  const [form, setForm] = useState({
    passwordPage: '',
    email: Cookies.get(`sp_content_portal_user_email_${page.id}`) || '',
    name: Cookies.get(`sp_content_portal_user_name_${page.id}`) || '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const requireName = !isLoggedIn() && page && page.require_name && !Cookies.get(`sp_content_portal_user_name_${page.id}`);
  const requireEmail = !isLoggedIn() && page && page.require_email && !Cookies.get(`sp_content_portal_user_email_${page.id}`);

  const validateForm = () => {
    const vErrors = {};

    if (providePassword && form.passwordPage.trim() === '') {
      vErrors.password = intl.formatMessage({ defaultMessage: 'Password is incorrect' });
    }

    if (requireEmail && !isValidEmail(form.email.trim())) {
      vErrors.email = intl.formatMessage({ defaultMessage: 'Enter a valid email address' });
    }

    if (requireName && form.name.trim() === '') {
      vErrors.name = intl.formatMessage({ defaultMessage: 'Enter your name' });
    }

    setErrors(vErrors);

    return Object.keys(vErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    const email = form.email.trim();
    const name = form.name.trim();
    const password = form.passwordPage;

    Cookies.set(`sp_content_portal_user_email_${page.id}`, email, { ...cookieParams, expires: 7 });
    Cookies.set(`sp_content_portal_user_name_${page.id}`, name, { ...cookieParams, expires: 7 });

    Promise.resolve(onSubmit(name, email, password))
      .finally(() => setLoading(false));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm({ ...form, [name]: value });
  };

  let icon = '';
  if (page.require_password) {
    icon = 'lock';
  }
  if (
    page.require_name ||
    page.require_email
  ) {
    if (page.require_password) {
      icon = 'user-lock';
    } else {
      icon = 'user';
    }
  }

  const fields = [];
  if (requireName) {
    fields.push(intl.formatMessage({
      defaultMessage: 'name',
    }));
  }
  if (requireEmail) {
    fields.push(intl.formatMessage({
      defaultMessage: 'email',
    }));
  }
  if (page.require_password) {
    fields.push(intl.formatMessage({
      defaultMessage: 'password',
    }));
  }

  const text = intl.formatMessage({
    defaultMessage: `{fieldsCount, select,
      1 {Enter your {field1} to access this Content Portal}
      2 {Enter your {field1} and {field2} to access this Content Portal}
      3 {Enter your {field1}, {field2} and {field3} to access this Content Portal}
      other {Enter your details to access this Content Portal}}`,
  }, {
    fieldsCount: fields.length,
    field1: fields[0],
    field2: fields[1],
    field3: fields[2],
  });

  return (
    <div className="layout page-unlock-access">
      <div>
        <div className="lock-section">
          <div className="lock-wrapper">
            <i className={`fa-regular fa-${icon}`} />
          </div>
          <h2>{text}</h2>
          <Form onChange={handleChange} onKeyPress={handleKeyPress}>
            <div className="access-input-container">
              {requireName && (
              <div>
                <Input
                  name="name"
                  id="inpName"
                  placeholder={intl.formatMessage({ defaultMessage: 'Enter your name' })}
                  invalid={!!errors.name}
                />
                <FormFeedback>
                  {errors.name}
                </FormFeedback>
              </div>
              )}
              {requireEmail && (
              <div>
                <Input
                  type="email"
                  name="email"
                  id="inpEmail"
                  placeholder={intl.formatMessage({ defaultMessage: 'Enter your email' })}
                  invalid={!!errors.email}
                />
                <FormFeedback>
                  {errors.email}
                </FormFeedback>
              </div>
              )}
              {providePassword && (
              <div>
                <Input
                  type="password"
                  name="passwordPage"
                  id="inpPasswordPage"
                  autoComplete="passwordPage"
                  onKeyPress={handleKeyPress}
                  invalid={!!errors.password || passwordError}
                  placeholder={intl.formatMessage({ defaultMessage: 'Enter your password' })}
                />
                <FormFeedback>
                  {errors.password || (passwordError && intl.formatMessage({ defaultMessage: 'Password is incorrect' }))}
                </FormFeedback>
              </div>
              )}
              <div>
                <Button
                  color="primary"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  <ProcessingSpinner show={loading} /> {intl.formatMessage({ defaultMessage: 'Submit' })}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
});

PageUnlockAccess.propTypes = {
  page: PropTypes.object.isRequired,
  providePassword: PropTypes.bool.isRequired,
  passwordError: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

PageUnlockAccess.defaultProps = {
  passwordError: false,
};

export default connect(null, null)(PageUnlockAccess);
